<template>
  <v-container fluid class="pa-5">
    <v-overlay :value="isLoading" absolute>
      <div class="d-flex flex-column align-center justify-center">
        <v-progress-circular indeterminate size="64" :width="5" />
        <span class="my-4">กำลังโหลดข้อมูล</span>
      </div>
    </v-overlay>

    <template v-if="!isLoading">
      <template v-if="admins.length === 0">
        <v-card class="card-container d-flex pa-6" elevation="0">
          <v-row>
            <v-col cols="12">
              <span class="title-text">จัดการผู้ดูแลระบบ</span>
            </v-col>

            <v-col
              cols="12"
              class="d-flex flex-column align-center justify-center"
              style="min-height: 88vh"
            >
              <div class="d-flex flex-column align-center">
                <v-img>
                  <img
                    src="@/assets/not_exist_search.svg"
                    alt="no-data"
                    width="103"
                    height="103"
                  />
                </v-img>
                <span class="no-data-text mb-4">
                  ยังไม่มีรายการผู้ดูแลระบบ
                </span>

                <v-btn
                  v-if="userRole === 'superadmin'"
                  color="#3D78C8"
                  dark
                  class="create-btn elevation-0"
                  @click="goToCreateAdmin"
                >
                  <v-icon left size="24">mdi-plus</v-icon>
                  สร้างผู้ดูแลระบบ
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </template>

      <template v-else>
        <v-card class="card-container pa-2" elevation="0">
          <v-card-title>
            <v-row>
              <v-col
                cols="12"
                md="6"
                class="d-flex align-center justify-center justify-md-start"
              >
                <span class="title-text">จัดการผู้ดูแลระบบ</span>
              </v-col>

              <v-col
                cols="12"
                md="6"
                class="d-flex align-center justify-center justify-md-end"
              >
                <v-btn
                  v-if="userRole === 'superadmin'"
                  color="#3b82f6"
                  dark
                  class="create-btn elevation-0"
                  @click="goToCreateAdmin"
                >
                  <v-icon left size="24">mdi-plus</v-icon>
                  สร้างผู้ดูแลระบบ
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>

          <v-card-text>
            <v-row>
              <v-col cols="12">
                <Card
                  :users-data="admins"
                  :selected-card-index="selectedCardIndex"
                  @select-card="onSelectCard"
                />
              </v-col>

              <v-col cols="12" class="mt-2">
                <Select
                  :search="search"
                  :selected-role="selectedRole"
                  :date-range="dateRange"
                  @update:search="search = $event"
                  @update:selected-role="selectedRole = $event"
                  @update:date-range="dateRange = $event"
                />
              </v-col>

              <v-col cols="12" class="mt-4">
                <template v-if="filteredAdmins.length > 0">
                  <Table
                    :admins="filteredAdmins"
                    :user-role="$store.getters.role"
                    @edit-role="openEditRole"
                    @delete="deleteAdmin"
                  />
                </template>

                <template v-else>
                  <v-row>
                    <v-col
                      cols="12"
                      class="d-flex flex-column align-center justify-center"
                      style="min-height: 45vh"
                    >
                      <div class="d-flex flex-column align-center">
                        <v-img>
                          <img
                            src="@/assets/not_exist_search.svg"
                            alt="no-data"
                            width="103.8"
                            height="103.8"
                          />
                        </v-img>
                        <span class="no-data-text mb-4">
                          ไม่พบข้อมูลที่ค้นหา กรุณาลองใหม่อีกครั้ง
                        </span>
                      </div>
                    </v-col>
                  </v-row>
                </template>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </template>
    </template>
  </v-container>
</template>

<script>
import Card from "@/components/AdminManagement/Card.vue";
import Select from "@/components/AdminManagement/Select.vue";
import Table from "@/components/AdminManagement/Table.vue";
import { getAdminsApi } from "@/services/api/adminServices";

export default {
  name: "AdminManagement",

  components: {
    Card,
    Select,
    Table,
  },

  data() {
    return {
      isLoading: false,
      admins: [],
      selectedCardIndex: 0,
      search: "",
      selectedRole: "all",
      dateRange: [],
    };
  },

  async created() {
    await this.loadAdmins();
  },

  computed: {
    userRole() {
      return (this.$store.getters.role || "").toString().trim().toLowerCase();
    },

    filteredAdmins() {
      const card = this.getCardByIndex(this.selectedCardIndex);

      const norm = (t) => (t || "").toString().trim().toLowerCase();
      const normalizeRole = (role) => {
        const r = norm(role);
        if (r === "admin") return "admin";
        if (r === "superadmin") return "superadmin";
        return "other";
      };

      let result = [...this.admins];

      if (card) {
        const key = card.key;

        if (key === "all_admins") {
          result = result.filter((u) =>
            ["admin", "superadmin"].includes(normalizeRole(u.role))
          );
        } else if (key === "admin_all") {
          result = result.filter((u) => normalizeRole(u.role) === "admin");
        } else if (key === "superadmin_all") {
          result = result.filter((u) => normalizeRole(u.role) === "superadmin");
        }
      }

      if (this.selectedRole && this.selectedRole !== "all") {
        const targetRole = this.selectedRole;
        result = result.filter((u) => normalizeRole(u.role) === targetRole);
      }

      if ((this.search || "").trim()) {
        const kw = this.search.toLowerCase().trim();

        result = result.filter((u) => {
          const fullName = [u.firstName, u.lastName]
            .map((x) => (x || "").trim())
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          const username = (u.username || "").toLowerCase();
          const email = (u.email || "").toLowerCase();

          return (
            fullName.includes(kw) || username.includes(kw) || email.includes(kw)
          );
        });
      }

      if (this.dateRange && this.dateRange.length === 2) {
        const [startRaw, endRaw] = this.dateRange;

        const startStr = new Date(startRaw).toISOString().slice(0, 10);
        const endStr = new Date(endRaw).toISOString().slice(0, 10);

        const [minStr, maxStr] =
          startStr <= endStr ? [startStr, endStr] : [endStr, startStr];

        result = result.filter((u) => {
          if (!u.createdAt) return false;
          const created = new Date(u.createdAt);
          if (isNaN(created)) return false;

          const createdStr = created.toISOString().slice(0, 10);
          return createdStr >= minStr && createdStr <= maxStr;
        });
      }

      return result;
    },
  },

  methods: {
    async loadAdmins() {
      this.isLoading = true;
      try {
        const data = await getAdminsApi();
        const users = Array.isArray(data.data) ? data.data : [];

        this.admins = users.filter((u) => {
          const r = (u.role || "").toString().trim().toLowerCase();
          return r === "admin" || r === "superadmin";
        });
      } catch (err) {
        console.error("[AdminManagement] loadAdmins error:", err);
        this.admins = [];
      } finally {
        this.isLoading = false;
      }
    },

    onSelectCard(index) {
      this.selectedCardIndex = index;
    },

    getCardByIndex(index) {
      const baseCards = [
        { key: "all_admins" },
        { key: "admin_all" },
        { key: "superadmin_all" },
      ];
      return baseCards[index] || null;
    },

    openEditRole(admin) {
      console.log("edit role", admin);
    },

    deleteAdmin(admin) {
      console.log("delete admin", admin);
    },

    goToCreateAdmin() {
      this.$router.push({ name: "admin_create" });
    },
  },
};
</script>

<style scoped>
.title-text {
  font-size: 24px;
  font-weight: 700;
  color: #1f2a39;
}
.no-data-text {
  font-size: 14px;
  font-weight: 400;
  color: #6b717f;
}
.card-container {
  border-radius: 16px !important;
  box-shadow: 0 0 2px 0 #1018280d !important;
}
.create-btn {
  font-size: 16px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 40px !important;
  text-transform: none !important;
}
@media (max-width: 960px) {
  .create-btn {
    width: 100% !important;
  }
}
</style>